import { TriangleAlert } from 'lucide-react';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@reflex/ui/components/ui/hover-card';
import { PageDescription } from '@reflex/ui/components/layout/Typography';
import { normalizeRepoSlug } from '@reflex/ui/lib/repo-slug';
import { cn } from '@reflex/ui/lib/utils';

export interface BlueprintRepoMismatchHintProps {
  /** `metadata.repo` of the selected blueprint. */
  blueprintRepo: string;
  /** The `owner/repo` slug the run clones. */
  repoSlug: string;
  className?: string;
}

/**
 * Shown beside the blueprint chip when the selected blueprint bakes in a
 * different repo than the run clones.
 *
 * The picker ranks by repo rather than filtering on it, so any blueprint is
 * selectable against any repo. That is deliberate, but it means a pick can
 * outlive the repo it was made for, and nothing else reports it:
 * `AgentRepoBlueprintWarning` only fires when the repo has no blueprint at
 * all, so a repo with one of its own stays silent while the launch boots from
 * another repo's image.
 *
 * A hint rather than a correction: the pick is explicit and outranks the
 * inferred default, so this surfaces the cost instead of quietly undoing it.
 */
export function BlueprintRepoMismatchHint({
  blueprintRepo,
  repoSlug,
  className,
}: BlueprintRepoMismatchHintProps) {
  return (
    <HoverCard openDelay={100} closeDelay={150}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          aria-label="Blueprint built for a different repo"
          data-testid="blueprint-repo-mismatch"
          className={cn(
            'inline-flex shrink-0 items-center rounded-xs text-status-accent-orange/90 transition-colors hover:text-status-accent-orange focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
            className,
          )}
        >
          <TriangleAlert className="icon-xs" aria-hidden="true" />
        </button>
      </HoverCardTrigger>
      <HoverCardContent align="start" className="w-72" data-testid="blueprint-repo-mismatch-card">
        <PageDescription className="mt-0 text-xs leading-relaxed text-foreground/90">
          This blueprint bakes in {normalizeRepoSlug(blueprintRepo)}, but the run clones{' '}
          {normalizeRepoSlug(repoSlug)}. The agent starts from an image built for a different repo.
        </PageDescription>
      </HoverCardContent>
    </HoverCard>
  );
}
